/**
 * Admin Geo API client
 *
 * Talks to /api/admin-geo.php (super-admin for writes). Credentialed (session
 * cookie). Manages the country/region list that drives the guest-estimate form.
 */

const API_BASE = '/api';

export interface AdminRegion {
  id?: number;
  name: string;
  code: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface AdminCountry {
  id: number;
  name: string;
  isoCode: string;
  regionLabel: string;
  /** Explicit display order (pinned); null = unset → alphabetical on the form. */
  sortOrder: number | null;
  isActive: boolean;
  /** Bundled label suggested for this ISO, or null when none is bundled. */
  suggestedRegionLabel?: string | null;
  /** True when the stored label is an explicit value that differs from the suggestion. */
  labelMismatch?: boolean;
  regions: AdminRegion[];
}

/** Result of a create / import — includes how many regions were merged. */
export interface CountryMutationResult {
  country: AdminCountry;
  imported?: number;
  updated?: number;
  sourceCount?: number;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchAdminGeo(): Promise<AdminCountry[]> {
  const res = await fetch(`${API_BASE}/admin-geo.php`, { credentials: 'include' });
  const data = await handle<{ countries: AdminCountry[] }>(res);
  return data.countries;
}

export async function createCountry(payload: {
  name: string;
  isoCode: string;
  regionLabel?: string;
  sortOrder?: number | null;
}): Promise<CountryMutationResult> {
  const res = await fetch(`${API_BASE}/admin-geo.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle<CountryMutationResult>(res);
}

export async function updateCountry(
  id: number,
  payload: {
    name?: string;
    regionLabel?: string;
    isActive?: boolean;
    sortOrder?: number | null;
    regions?: AdminRegion[];
  },
): Promise<{ country: AdminCountry }> {
  const res = await fetch(`${API_BASE}/admin-geo.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle<{ country: AdminCountry }>(res);
}

/** Pull/refresh a country's regions from the external source (adds missing). */
export async function importCountryRegions(id: number): Promise<CountryMutationResult> {
  const res = await fetch(`${API_BASE}/admin-geo.php?action=import&id=${id}`, {
    method: 'POST',
    credentials: 'include',
  });
  return handle<CountryMutationResult>(res);
}

export async function deleteCountry(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/admin-geo.php?id=${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error || `Delete failed (${res.status})`);
  }
}
